import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Centralized payout/payment readiness status for providers.
 *
 * Used by both onboarding (PaymentSetupStep) and dashboard (BusinessSettings)
 * to ensure consistent status display across the app.
 */

export type PayoutStatus =
  | 'not_started'          // No connected account exists
  | 'requirements_due'     // Account exists but Stripe needs more info
  | 'pending_verification' // Details submitted, waiting for Stripe review
  | 'payments_enabled'     // Charges enabled, can accept payments
  | 'restricted';          // Charges/payouts disabled due to corrective action

export interface PayoutStatusInfo {
  status: PayoutStatus;
  accountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirementsDue: string[];
  disabledReason: string | null;
}

export function derivePayoutStatus(account: {
  id?: string | null;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  details_submitted?: boolean;
  requirements?: {
    currently_due?: string[];
    pending_verification?: string[];
    disabled_reason?: string | null;
  };
} | null): PayoutStatusInfo {
  if (!account?.id) {
    return {
      status: 'not_started',
      accountId: null,
      chargesEnabled: false,
      payoutsEnabled: false,
      detailsSubmitted: false,
      requirementsDue: [],
      disabledReason: null,
    };
  }

  const chargesEnabled = account.charges_enabled ?? false;
  const payoutsEnabled = account.payouts_enabled ?? false;
  const detailsSubmitted = account.details_submitted ?? false;
  const currentlyDue = account.requirements?.currently_due ?? [];
  const disabledReason = account.requirements?.disabled_reason ?? null;

  let status: PayoutStatus;

  if (disabledReason) {
    // Stripe has explicitly disabled the account with a stated reason
    status = 'restricted';
  } else if (chargesEnabled) {
    status = 'payments_enabled';
  } else if (currentlyDue.length > 0) {
    status = 'requirements_due';
  } else if (detailsSubmitted) {
    // Details submitted, no requirements due, but charges not yet enabled —
    // Stripe is reviewing the account
    status = 'pending_verification';
  } else {
    status = 'requirements_due';
  }

  return {
    status,
    accountId: account.id ?? null,
    chargesEnabled,
    payoutsEnabled,
    detailsSubmitted,
    requirementsDue: currentlyDue,
    disabledReason,
  };
}

export function usePayoutStatus() {
  const [statusInfo, setStatusInfo] = useState<PayoutStatusInfo>({
    status: 'not_started',
    accountId: null,
    chargesEnabled: false,
    payoutsEnabled: false,
    detailsSubmitted: false,
    requirementsDue: [],
    disabledReason: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setStatusInfo(derivePayoutStatus(null));
        return;
      }

      const { data, error } = await supabase.functions.invoke('stripe-connect', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { action: 'getConnectStatus' },
      });

      if (error || !data?.account) {
        setStatusInfo(derivePayoutStatus(null));
        return;
      }

      setStatusInfo(derivePayoutStatus(data.account));
    } catch (err) {
      console.error('Error fetching payout status:', err);
      setStatusInfo(derivePayoutStatus(null));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { ...statusInfo, isLoading, refetch: fetchStatus };
}
